/**
 * O acesso ao Financeiro da pessoa logada: se ela abre o módulo e em quais
 * unidades. A regra mora em `@/lib/financeiro/acesso`; aqui só a leitura da
 * chave das unidades, feita uma vez por sessão e compartilhada pelo menu, pelo
 * seletor do topo, pelo portão da rota e pelo módulo. Os quatro respondem com a
 * mesma régua porque leem o mesmo valor.
 *
 * A leitura é `credit_v2_fin_settings` com `fin_enabled = true`, só das unidades
 * da pessoa, e só quando o papel depende da chave (administrador e gerente de
 * rede). O super_admin não espera leitura nenhuma; vendedor, backoffice e
 * agência também não.
 *
 * ⚠️ CONTRATO COM O BANCO. A leitura passa pela política da tabela, que hoje
 * deixa qualquer membro ativo da unidade ler a linha dela. O que esta tela
 * precisa é menos que isso: o administrador e o gerente de rede lerem a linha
 * das unidades em que o vínculo está ativo. Como o filtro já pede só as
 * ligadas, a resposta é a mesma se o banco passar a devolver só as ligadas.
 *
 * 🚨 ENQUANTO A CHAVE CARREGA, O ESTADO É "carregando", NUNCA "não pode". O
 * portão da rota mostra a espera em vez de "Acesso negado", e o menu só ganha
 * o item quando a resposta chega.
 *
 * Releitura (lista de unidades mudou, "Tentar de novo"): o valor anterior da
 * mesma pessoa continua valendo enquanto a nova resposta não chega. É seguro
 * porque a regra cruza a chave com a lista de unidades ATUAL: unidade nova só
 * entra quando a resposta nova chegar, e unidade que saiu da lista sai na hora.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { supabase } from "@/lib/supabase/client"
import { TenantContext } from "@/contexts/TenantContext"
import { isModuloNaoInstalado } from "@/lib/financeiro/query"
import {
  acessoAoFinanceiro,
  papelPelaChaveDaUnidade,
  type AcessoAoFinanceiro,
} from "@/lib/financeiro/acesso"

export interface AcessoAoFinanceiroValor extends AcessoAoFinanceiro {
  /**
   * `carregando`: a sessão ou a chave das unidades ainda não chegou.
   * `falhou`: não foi possível ler a chave; `pode` é falso e a tela oferece
   * tentar de novo, sem dizer que a pessoa não tem acesso.
   */
  estado: "carregando" | "pronto" | "falhou"
  tentarDeNovo: () => void
}

export const AcessoAoFinanceiroContext = createContext<AcessoAoFinanceiroValor | null>(null)

/**
 * Os ids das unidades, entre as pedidas, com o Financeiro ligado. `null` quando a
 * leitura falhou. Banco sem o módulo instalado responde "nenhuma".
 */
export async function lerUnidadesComFinanceiro(tenantIds: readonly string[]): Promise<string[] | null> {
  if (tenantIds.length === 0) return []
  try {
    const { data, error } = (await supabase
      .from("credit_v2_fin_settings")
      .select("tenant_id")
      .eq("fin_enabled", true)
      .in("tenant_id", tenantIds)) as { data: { tenant_id: string }[] | null; error: unknown }
    if (error) {
      if (isModuloNaoInstalado(error)) return []
      console.error("[financeiro] leitura da chave das unidades", error)
      return null
    }
    return (data ?? []).map((linha) => linha.tenant_id)
  } catch (erro) {
    console.error("[financeiro] leitura da chave das unidades", erro)
    return null
  }
}

interface Leitura {
  pessoa: string
  pergunta: string
  ligadas: string[] | null
}

export function AcessoAoFinanceiroProvider({ children }: { children: ReactNode }) {
  const sessao = useContext(TenantContext)
  const pessoa = sessao?.user?.id ?? null
  const papel = sessao?.role
  const unidades = useMemo(() => sessao?.tenants ?? [], [sessao?.tenants])
  const dadosProntos = sessao?.isDataReady ?? false
  const pelaChave = papelPelaChaveDaUnidade(papel)

  // Texto, não o array: `tenants` troca de identidade quando a unidade ativa é
  // editada, sem a lista mudar. Usado direto, releria a chave à toa.
  const idsDasUnidades = useMemo(
    () =>
      unidades
        .map((u) => u.id)
        .sort()
        .join(","),
    [unidades],
  )
  const [tentativa, setTentativa] = useState(0)
  const pergunta = `${pessoa ?? "-"}|${idsDasUnidades}|${tentativa}`
  const [lido, setLido] = useState<Leitura | null>(null)

  useEffect(() => {
    if (!dadosProntos || !pessoa || !pelaChave) return
    let vigente = true
    void lerUnidadesComFinanceiro(idsDasUnidades ? idsDasUnidades.split(",") : []).then((ligadas) => {
      if (vigente) setLido({ pessoa, pergunta, ligadas })
    })
    return () => {
      vigente = false
    }
  }, [dadosProntos, pessoa, pelaChave, idsDasUnidades, pergunta])

  const tentarDeNovo = useCallback(() => setTentativa((n) => n + 1), [])

  const valor = useMemo<AcessoAoFinanceiroValor>(() => {
    const carregando: AcessoAoFinanceiroValor = {
      estado: "carregando",
      pode: false,
      motivo: null,
      unidadesLiberadas: [],
      tentarDeNovo,
    }
    if (!dadosProntos) return carregando
    if (!pessoa || !pelaChave) return { estado: "pronto", ...acessoAoFinanceiro(papel, unidades, []), tentarDeNovo }

    const daPessoa = lido?.pessoa === pessoa ? lido : null
    const exata = daPessoa?.pergunta === pergunta ? daPessoa : null
    if (exata && exata.ligadas === null) {
      return { estado: "falhou", pode: false, motivo: null, unidadesLiberadas: [], tentarDeNovo }
    }
    // A resposta desta pergunta ou, enquanto ela não chega, a anterior da mesma
    // pessoa (ver "Releitura" no topo do arquivo).
    const ligadas = exata?.ligadas ?? daPessoa?.ligadas ?? null
    if (ligadas === null) return carregando
    return { estado: "pronto", ...acessoAoFinanceiro(papel, unidades, ligadas), tentarDeNovo }
  }, [dadosProntos, pessoa, pelaChave, papel, unidades, lido, pergunta, tentarDeNovo])

  return <AcessoAoFinanceiroContext.Provider value={valor}>{children}</AcessoAoFinanceiroContext.Provider>
}

/**
 * O acesso ao Financeiro da pessoa logada.
 *
 * Fora do provedor (telas montadas sozinhas nos testes), responde só pelo papel:
 * o super_admin abre, os outros não. No app o provedor sempre existe
 * (`AppProviders`).
 */
export function useAcessoAoFinanceiro(): AcessoAoFinanceiroValor {
  const valor = useContext(AcessoAoFinanceiroContext)
  const sessao = useContext(TenantContext)
  if (valor) return valor
  return { estado: "pronto", ...acessoAoFinanceiro(sessao?.role, [], []), tentarDeNovo: semNada }
}

function semNada() {}
